import { conns } from '@cfg/axiosConfig.tsx';
import type { StaffDirectoryEntry } from '@erp/services/api/serviceStaff';
import type { SaleOrder } from '@eshop/types/order.types.ts';
export { fetchSpaces, useSpaces } from '@erp/spaces/api/spaces.api';
export type { Space, SpaceListResponse } from '@erp/spaces/api/spaces.api';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { SalePaymentCreatePayload } from '@erp/pos/types';

type AppointmentStatus = 'draft' | 'scheduled' | 'confirmed' | 'attended' | 'no_show' | 'canceled';

export interface AppointmentService {
  id: number;
  name: string;
  is_active: boolean;
  is_appointment_based: boolean;
  default_duration_minutes?: number | null;
  default_professional_id?: number | null;
  default_space_id?: number | null;
  allow_quantity_gt1: boolean;
  history_enabled: boolean;
  price_value?: number | null;
  price_currency?: string | null;
  updated_at?: string | null;
  professional_ids?: number[];
  spaces?: ServiceSpaceLink[];
  availability_rules?: ServiceAvailabilityWindow[];
  category_ids?: number[];
}

export interface AppointmentServiceListResponse {
  items: AppointmentService[];
  total: number;
  page: number;
  page_size: number;
}

export interface ServiceSpaceLink {
  space_id: number;
  is_primary: boolean;
}

export interface ServiceAvailabilityWindow {
  days: string[];
  start_time: string;
  end_time: string;
}

export type AppointmentWeekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface AppointmentServiceFilters {
  q?: string;
  page?: number;
  pageSize?: number;
  onlyAppointments?: boolean;
  categoryId?: number;
  categoryIds?: number[];
  staffUserId?: number;
  spaceId?: number;
  weekday?: AppointmentWeekday;
}

export const appointmentServicesQueryKey = ['erp', 'appointments', 'services'] as const;

/**
 * Objetivo: Normalizar los filtros recibidos desde la UI para la consulta de servicios de turnos.
 * Entradas:
 *   - filters: Filtros opcionales enviados por el consumidor del hook, con atributos en camelCase.
 * Salidas: Objeto con las claves y valores listos para ser utilizados por la API (`snake_case`).
 * Requerimientos: Mantener `only_appointments` activo por omisión y aplicar valores por defecto a la paginación.
 */
const normalizeServiceFilters = (filters?: AppointmentServiceFilters) => {
  if (!filters) {
    return { only_appointments: true, page_size: 100 };
  }

  const { q, page, pageSize, categoryId, categoryIds, staffUserId, spaceId, weekday } = filters;
  return {
    q: q?.trim() || undefined,
    page,
    page_size: pageSize ?? 100,
    only_appointments: true,
    category_id: categoryId,
    category_ids: categoryIds,
    staff_user_id: staffUserId,
    space_id: spaceId,
    weekday,
  };
};

/**
 * Objetivo: Obtener desde el backend la lista de servicios que pueden ser agendados.
 * Entradas:
 *   - filters: Criterios opcionales para limitar la búsqueda de servicios.
 * Salidas: Promesa que resuelve con la respuesta tipada `AppointmentServiceListResponse`.
 * Requerimientos: Utilizar el conector centralizado `conns` y respetar la normalización de filtros.
 */
export const fetchAppointmentServices = async (
  filters?: AppointmentServiceFilters
): Promise<AppointmentServiceListResponse> => {
  const params = normalizeServiceFilters(filters);
  const { data } = await conns<AppointmentServiceListResponse>('erp:appointments:services').get(params);
  return data;
};

/**
 * Objetivo: Exponer un hook React Query para consumir servicios de turnos con caching y revalidación.
 * Entradas:
 *   - filters: Filtros opcionales que modifican la clave del query para segmentar los datos.
 *   - options: Configuración adicional del query (staleTime, select, etc.).
 * Salidas: Resultado completo de `useQuery`, incluyendo estado, datos y helpers.
 * Requerimientos: Mantener una clave estable y actualizar automáticamente cuando cambien los filtros.
 */
export const useAppointmentServices = (
  filters?: AppointmentServiceFilters,
  options?: Partial<UseQueryOptions<AppointmentServiceListResponse, Error, AppointmentServiceListResponse>>
): UseQueryResult<AppointmentServiceListResponse, Error> => {
  return useQuery<AppointmentServiceListResponse, Error, AppointmentServiceListResponse>({
    queryKey: filters ? [...appointmentServicesQueryKey, filters] : appointmentServicesQueryKey,
    queryFn: () => fetchAppointmentServices(filters),
    staleTime: 30_000,
    ...options,
  });
};

export interface ProfessionalListResponse {
  items: StaffDirectoryEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface ProfessionalFilters {
  q?: string;
  includeInactive?: boolean;
  page?: number;
  pageSize?: number;
}

export const professionalsQueryKey = ['erp', 'appointments', 'professionals'] as const;

/**
 * Objetivo: Adaptar los filtros de profesionales a la forma esperada por el backend.
 * Entradas:
 *   - filters: Datos opcionales recibidos desde la UI.
 * Salidas: Objeto listo para enviarse como query params en la request HTTP.
 * Requerimientos: Aplicar valores por defecto y sanear cadenas vacías.
 */
const normalizeProfessionalFilters = (filters?: ProfessionalFilters) => {
  if (!filters) {
    return { include_inactive: false, page_size: 200 };
  }

  const { q, includeInactive, page, pageSize } = filters;
  return {
    q: q?.trim() || undefined,
    include_inactive: includeInactive ?? false,
    page,
    page_size: pageSize ?? 200,
  };
};

/**
 * Objetivo: Recuperar la lista de profesionales que pueden atender turnos.
 * Entradas:
 *   - filters: Filtros opcionales para paginar o buscar profesionales.
 * Salidas: Promesa con la respuesta del backend tipada como `ProfessionalListResponse`.
 * Requerimientos: Invocar al endpoint definido en `conns` y reutilizar la normalización de filtros.
 */
export const fetchProfessionals = async (filters?: ProfessionalFilters): Promise<ProfessionalListResponse> => {
  const params = normalizeProfessionalFilters(filters);
  const { data } = await conns<ProfessionalListResponse>('erp:appointments:professionals').get(params);
  return data;
};

/**
 * Objetivo: Entregar un hook con caching para consultar profesionales desde la vista de turnos.
 * Entradas:
 *   - filters: Parámetros opcionales que se combinan con la clave del query.
 *   - options: Configuración opcional para personalizar el comportamiento del query.
 * Salidas: Resultado de `useQuery` con estados, datos y utilidades asociadas.
 * Requerimientos: Definir `staleTime` razonable y derivar la función de obtención de datos.
 */
export const useProfessionals = (
  filters?: ProfessionalFilters,
  options?: Partial<UseQueryOptions<ProfessionalListResponse, Error, ProfessionalListResponse>>
): UseQueryResult<ProfessionalListResponse, Error> => {
  return useQuery<ProfessionalListResponse, Error, ProfessionalListResponse>({
    queryKey: filters ? [...professionalsQueryKey, filters] : professionalsQueryKey,
    queryFn: () => fetchProfessionals(filters),
    staleTime: 30_000,
    ...options,
  });
};

export interface AppointmentServiceLine {
  id?: number;
  service_id: number;
  quantity: number;
  duration_minutes?: number | null;
  unit_price?: number | null;
  notes?: string | null;
  /** DEPRECATED: legado, el backend ignora este valor. */
  start_offset_minutes?: number | null;
  start_at?: string | null;
  end_at?: string | null;
  created_at?: string;
  service_name?: string | null;
}

export interface Appointment {
  id: number;
  customer_id: number | null;
  customer_name?: string | null;
  customer_username?: string | null;
  customer_document_number?: string | null;
  attendee_count?: number | null;
  max_attendees?: number | null;
  is_group?: boolean | null;
  staff_user_id: number | null;
  space_id: number | null;
  sale_id: number | null;
  status: AppointmentStatus;
  start_at: string;
  end_at: string;
  duration_minutes?: number | null;
  notes?: string | null;
  linked_group_id?: string | null;
  version: number;
  created_at: string;
  updated_at: string;
  service_lines: AppointmentServiceLine[];
  warnings: string[];
  history_entries_count?: number;
  latest_history_note?: string | null;
  latest_history_entry_id?: number | null;
  latest_history_recorded_by?: string | null;
}

export interface GroupEventProjection {
  source: 'group_event';
  group_event_key: string;
  group_id: number;
  pattern_id: number;
  service_id: number;
  local_date: string;
  start_at: string;
  end_at: string;
  staff_user_id: number;
  space_id: number;
  capacity: number;
  status: string;
  override_id?: number | null;
  replacement_of_key?: string | null;
  expected_count: number;
  drop_in_count: number;
  group_name?: string;
}

export interface GroupClass {
  id: number;
  name: string;
  service_id: number;
  staff_user_id: number;
  capacity: number;
  status: string;
  allow_subscription_access: boolean;
  allow_drop_in_payment: boolean;
  notes?: string | null;
}

export interface GroupClassCreatePayload {
  name: string;
  service_id: number;
  staff_user_id: number;
  capacity: number;
  status?: string;
  allow_subscription_access?: boolean;
  allow_drop_in_payment?: boolean;
  notes?: string | null;
}

export type GroupClassUpdatePayload = Partial<GroupClassCreatePayload>;

export interface GroupClassSetupPayload {
  group: Omit<GroupClassCreatePayload, 'name'> & { name?: string | null };
  patterns: GroupSchedulePatternCreatePayload[];
}

export interface GroupClassSetupResult {
  group: GroupClass;
  patterns: GroupSchedulePattern[];
}

export interface GroupSchedulePattern {
  id: number;
  group_id: number;
  days_of_week: number[];
  start_time: string;
  duration_minutes: number;
  starts_on: string;
  ends_on?: string | null;
  timezone: string;
  frequency: string;
  interval_weeks: number;
  is_active: boolean;
  space_id: number;
  supersedes_pattern_id?: number | null;
  created_by?: number | null;
  updated_by?: number | null;
}

export interface GroupSchedulePatternCreatePayload {
  days_of_week: number[];
  start_time: string;
  duration_minutes: number;
  starts_on: string;
  ends_on?: string | null;
  timezone?: string;
  frequency?: string;
  interval_weeks?: number;
  is_active?: boolean;
  space_id: number;
}

export interface GroupSchedulePatternRevisionPayload {
  effective_from: string;
  days_of_week: number[];
  start_time: string;
  duration_minutes: number;
  ends_on?: string | null;
  timezone?: string;
  frequency?: string;
  interval_weeks?: number;
  space_id: number;
}

export interface GroupSchedulePatternRevisionResult {
  mode: 'split' | 'updated_in_place';
  closed_pattern: GroupSchedulePattern | null;
  active_pattern: GroupSchedulePattern;
}

export interface GroupParticipant {
  id: number;
  group_id: number;
  customer_id: number;
  customer_name?: string | null;
  subscription_id?: number | null;
  valid_from: string;
  valid_to?: string | null;
  participation_days?: number[] | null;
  status: string;
  reserved_capacity: boolean;
  notes?: string | null;
}

export interface GroupParticipantCreatePayload {
  customer_id: number;
  subscription_id?: number | null;
  valid_from: string;
  valid_to?: string | null;
  participation_days?: number[] | null;
  status?: string;
  reserved_capacity?: boolean;
  notes?: string | null;
}

export interface GroupEventParticipant {
  id: number;
  group_id: number;
  pattern_id: number;
  group_event_key: string;
  customer_id: number;
  participant_type: string;
  subscription_id?: number | null;
  group_participant_id?: number | null;
  notes?: string | null;
  attendance_status: string;
  coverage_status: string;
  subscription_usage_id?: number | null;
  sale_order_id?: number | null;
}

export interface GroupEventOperationAllowedActions {
  can_add_participant: boolean;
  can_mark_attendance: boolean;
  can_mark_attendance_without_payment: boolean;
  can_mark_free_attendance: boolean;
  can_charge_drop_in: boolean;
  can_reverse_usage: boolean;
  can_view_pos: boolean;
}

export interface GroupEventOperationDefaults {
  subscription_id?: number | null;
  cycle_id?: number | null;
  item_id?: number | null;
}

export interface GroupEventOperationParticipant {
  row_key: string;
  customer_id: number;
  customer_name: string;
  participant_type: string;
  group_participant_id?: number | null;
  group_event_participant_id?: number | null;
  subscription_id?: number | null;
  cycle_id?: number | null;
  item_id?: number | null;
  attendance_status: string;
  coverage_status: string;
  access_allowed: boolean;
  access_label: string;
  subscription_name?: string | null;
  subscription_usage_id?: number | null;
  sale_order_id?: number | null;
  manual_authorization_reason?: string | null;
  notes?: string | null;
}

export interface GroupEventOperationDetail {
  event: GroupEventProjection;
  defaults: GroupEventOperationDefaults;
  allowed_actions: GroupEventOperationAllowedActions;
  participants: GroupEventOperationParticipant[];
}

export interface GroupEventParticipantCreatePayload {
  group_event_key: string;
  customer_id: number;
  participant_type?: string;
  subscription_id?: number | null;
  group_participant_id?: number | null;
  notes?: string | null;
}

export interface GroupEventOverride {
  id: number;
  group_id: number;
  pattern_id: number;
  group_event_key: string;
  override_type: string;
  reason?: string | null;
  original_local_date: string;
  original_start_time: string;
  replacement_local_date?: string | null;
  replacement_start_time?: string | null;
  replacement_duration_minutes?: number | null;
  replacement_staff_user_id?: number | null;
  replacement_space_id?: number | null;
  replacement_event_key?: string | null;
}

export interface GroupEventOverrideCreatePayload {
  group_event_key: string;
  override_type: string;
  reason?: string | null;
  replacement_local_date?: string | null;
  replacement_start_time?: string | null;
  replacement_duration_minutes?: number | null;
  replacement_staff_user_id?: number | null;
  replacement_space_id?: number | null;
  replacement_event_key?: string | null;
}

export interface AttendancePresentPayload {
  attendance_mode?: 'standard' | 'without_payment' | 'free';
  group_event_participant_id: number;
  subscription_id?: number;
  cycle_id?: number;
  item_id?: number;
  quantity?: string | number;
  notes?: string | null;
}

export interface AttendanceUsageResult {
  subscription_usage_id: number;
  group_event_participant_id: number;
  source: string;
  quantity: string;
}

export interface AttendanceReversePayload {
  subscription_usage_id: number;
  reason?: string | null;
}

export interface AttendanceAbsentPayload {
  group_event_participant_id: number;
  reason?: string | null;
}

export interface AttendanceResetPayload {
  group_event_participant_id: number;
  reason?: string | null;
}

export interface DropInOrderPayload {
  group_event_participant_id: number;
  item_id: number;
  amount: string | number;
  quantity?: string | number;
}

export interface DropInOrderResult {
  sale_order_id: number;
  group_event_participant_id: number;
}

export type AppointmentAgendaItem = Appointment | GroupEventProjection;

export const isGroupEventProjection = (appointment: AppointmentAgendaItem): appointment is GroupEventProjection => {
  return 'source' in appointment && appointment.source === 'group_event';
};

export const isRealAppointment = (appointment: AppointmentAgendaItem): appointment is Appointment => {
  return !isGroupEventProjection(appointment);
};

export interface AppointmentListResponse {
  appointments: Appointment[];
}

export interface AppointmentExpandedResponse {
  appointments: Appointment[];
  group_events?: GroupEventProjection[];
}

export interface AppointmentAttendee {
  id: number;
  customer_id: number;
  customer_name?: string | null;
  subscription_id?: number | null;
  is_paid?: boolean | null;
  status: string;
  added_at: string;
}

export interface AppointmentAttendeeCreatePayload {
  customer_id: number;
  subscription_id?: number | null;
}

export const appointmentAttendeesQueryKey = ['erp', 'appointments', 'attendees'] as const;

export interface AppointmentCancelPayload {
  version: number;
}

export interface AppointmentReactivatePayload {
  version: number;
}

export interface PlanningSplitPayload {
  appointment_id: number;
  service_id: number;
  staff_user_id: number;
  space_id: number;
  customer_id: number | null;
  duration_minutes: number;
  duration_override_minutes?: number;
  start_at: string;
  /** DEPRECATED: legado, el backend ignora este valor. */
  start_offset_minutes?: number;
  order: number;
  sequence_id?: string | null;
  locked?: boolean;
  quantity?: number;
  base_duration_minutes?: number;
  notes?: string | null;
}

export interface PlanningGroupPayload {
  group_id: string;
  sequence?: number;
  professional_id?: number | null;
  preferred_space_id?: number | null;
  duration_override_minutes?: number;
  pending_assignment?: boolean;
  anchor_start_at: string;
  splits: PlanningSplitPayload[];
}

export interface PlanningResolutionPayload {
  appointment_id: number;
  action: 'set_start' | 'change_space';
  start_at?: string | null;
  space_id?: number | null;
}

export interface PlanningConflictResponse {
  conflict_id: string;
  group_id: string;
  appointment_id: number;
  conflict_type: 'staff' | 'space' | 'customer';
  resource_id?: number | null;
  message: string;
  suggestions: PlanningResolutionPayload[];
}

export interface PlanningSplitResponse extends PlanningSplitPayload {
  end_at: string;
}

export interface PlanningGroupResponse {
  group_id: string;
  splits: PlanningSplitResponse[];
}

export interface PlanningDominoRequest {
  groups: PlanningGroupPayload[];
  resolutions?: PlanningResolutionPayload[];
}

export interface PlanningDominoResponse {
  groups: PlanningGroupResponse[];
  conflicts: PlanningConflictResponse[];
}

export interface AppointmentChargeSaleItemDraft {
  item_id: number;
  qty: number;
  unit_price: number;
  discount_coef?: number | null;
  appointment_id?: number | null;
}

export interface AppointmentChargeSalePayload {
  items?: AppointmentChargeSaleItemDraft[];
  customer_id?: number | null;
  shipping_method_id?: number | null;
  address_id?: number | null;
  takeaway?: boolean;
  payment_method_id?: number | null;
  payments?: SalePaymentCreatePayload[];
  coupon_codes?: string[];
  contact_email?: string | null;
  contact_phone?: string | null;
  guest_profile_id?: number | null;
  guest_address_id?: number | null;
  comment?: string | null;
}

export interface AppointmentChargePayload {
  version: number;
  sale?: AppointmentChargeSalePayload | null;
}

export interface AppointmentChargeResponse {
  appointment: Appointment;
  sale: SaleOrder;
  warnings: string[];
}

export interface AppointmentLinkedChargeItemPayload {
  appointment_id: number;
  version: number;
}

export interface AppointmentLinkedChargePayload {
  appointments: AppointmentLinkedChargeItemPayload[];
  sale?: AppointmentChargeSalePayload | null;
}

export interface AppointmentLinkedChargeResponse {
  appointments: Appointment[];
  sale: SaleOrder;
  warnings: string[];
}

export interface AppointmentHistoryStaff {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  username?: string | null;
}

export interface AppointmentHistorySaleItem {
  id: number;
  item_id?: number | null;
  name?: string | null;
}

export interface AppointmentHistoryFollowUp {
  id: number;
  start_at: string;
  status: AppointmentStatus;
}

export interface AppointmentHistoryEntry {
  id: number;
  appointment_id: number;
  staff_user_id: number;
  duration_minutes?: number | null;
  notes?: string | null;
  sale_item_id?: number | null;
  follow_up_appointment_id?: number | null;
  created_at: string;
  created_by?: number | null;
  staff: AppointmentHistoryStaff;
  sale_item?: AppointmentHistorySaleItem | null;
  follow_up?: AppointmentHistoryFollowUp | null;
}

export interface AppointmentHistoryListResponse {
  history: AppointmentHistoryEntry[];
}

export interface AppointmentFilters {
  start?: string;
  end?: string;
  staffUserId?: number;
  spaceId?: number;
}

export const appointmentsListKey = ['erp', 'appointments', 'list'] as const;
export const appointmentsExpandedQueryKey = ['erp', 'appointments', 'expanded'] as const;
export const groupClassesQueryKey = ['erp', 'group-classes'] as const;
export const groupClassPatternsQueryKey = ['erp', 'group-classes', 'patterns'] as const;
export const groupClassEventsQueryKey = ['erp', 'group-classes', 'events'] as const;
export const groupClassOperationQueryKey = ['erp', 'group-classes', 'operation'] as const;
export const groupClassParticipantsQueryKey = ['erp', 'group-classes', 'participants'] as const;

const invalidateAppointmentsQueries = (client: ReturnType<typeof useQueryClient>): void => {
  void client.invalidateQueries({ queryKey: appointmentsListKey });
  void client.invalidateQueries({ queryKey: appointmentsExpandedQueryKey });
};

const invalidateGroupClassQueries = (client: ReturnType<typeof useQueryClient>): void => {
  void client.invalidateQueries({ queryKey: groupClassesQueryKey });
  void client.invalidateQueries({ queryKey: groupClassPatternsQueryKey });
  void client.invalidateQueries({ queryKey: groupClassEventsQueryKey });
  void client.invalidateQueries({ queryKey: groupClassOperationQueryKey });
  void client.invalidateQueries({ queryKey: groupClassParticipantsQueryKey });
  invalidateAppointmentsQueries(client);
};

export const cacheGroupClassSetupResult = (
  client: ReturnType<typeof useQueryClient>,
  result: GroupClassSetupResult,
): void => {
  client.setQueryData<GroupClass[]>(groupClassesQueryKey, (current = []) => {
    const withoutCreatedGroup = current.filter((group) => group.id !== result.group.id);
    return [...withoutCreatedGroup, result.group];
  });
  client.setQueryData<GroupSchedulePattern[]>(
    [...groupClassPatternsQueryKey, result.group.id],
    result.patterns,
  );
};

/**
 * Objetivo: Transformar los filtros de turnos generales en parámetros compatibles con la API.
 * Entradas:
 *   - filters: Valores opcionales provenientes de la capa de presentación.
 * Salidas: Objeto plano con los campos esperados por el backend.
 * Requerimientos: Mantener claves en `snake_case` y respetar valores `undefined` cuando no aplican.
 */
const normalizeFilters = (filters?: AppointmentFilters) => {
  if (!filters) {
    return {};
  }
  const { start, end, staffUserId, spaceId } = filters;
  const normalizedStaffId =
    typeof staffUserId === 'number' && Number.isFinite(staffUserId) && staffUserId > 0
      ? Math.trunc(staffUserId)
      : undefined;
  const normalizedSpaceId =
    typeof spaceId === 'number' && Number.isFinite(spaceId) && spaceId > 0 ? Math.trunc(spaceId) : undefined;
  return {
    start,
    end,
    staff_user_id: normalizedStaffId,
    space_id: normalizedSpaceId,
  };
};

/**
 * Objetivo: Consultar la agenda de turnos dentro de un rango determinado.
 * Entradas:
 *   - filters: Criterios opcionales como fechas o recursos involucrados.
 * Salidas: Promesa con la lista de turnos (`AppointmentListResponse`).
 * Requerimientos: Usar `conns` y evitar llamadas innecesarias cuando no hay cambios.
 */
export const fetchAppointments = async (filters?: AppointmentFilters): Promise<AppointmentListResponse> => {
  const params = normalizeFilters(filters);
  const { data } = await conns<AppointmentListResponse>('erp:appointments:list').get(params);
  return data;
};

export interface FetchAppointmentsExpandedParams {
  start: string;
  end: string;
  staffUserId?: number;
  spaceId?: number;
}

export const fetchAppointmentsExpanded = async (
  params: FetchAppointmentsExpandedParams
): Promise<AppointmentExpandedResponse> => {
  const { start, end, staffUserId, spaceId } = params;
  const { data } = await conns<AppointmentExpandedResponse>('erp:appointments:list').get({
    start,
    end,
    staff_user_id: staffUserId,
    space_id: spaceId,
  });
  return data;
};

export const fetchGroupClasses = async (): Promise<GroupClass[]> => {
  const { data } = await conns<GroupClass[]>('erp:group_classes:base').get();
  return data;
};

export const createGroupClass = async (payload: GroupClassCreatePayload): Promise<GroupClass> => {
  const { data } = await conns<GroupClass>('erp:group_classes:base').post(payload);
  return data;
};

export const updateGroupClass = async (groupId: number, payload: GroupClassUpdatePayload): Promise<GroupClass> => {
  const { data } = await conns<GroupClass>('erp:group_classes:base', [groupId]).patch(payload);
  return data;
};

export const createGroupClassSetup = async (
  payload: GroupClassSetupPayload,
): Promise<GroupClassSetupResult> => {
  const { data } = await conns<GroupClassSetupResult>('erp:group_classes:base', ['setup']).post(payload);
  return data;
};

export const regenerateGroupClassName = async (groupId: number): Promise<GroupClass> => {
  const { data } = await conns<GroupClass>('erp:group_classes:base', [groupId, 'regenerate-name']).post();
  return data;
};

export const createGroupSchedulePattern = async (
  groupId: number,
  payload: GroupSchedulePatternCreatePayload
): Promise<GroupSchedulePattern> => {
  const { data } = await conns<GroupSchedulePattern>('erp:group_classes:base', [groupId, 'patterns']).post(payload);
  return data;
};

export const updateGroupSchedulePattern = async (
  groupId: number,
  patternId: number,
  payload: Partial<GroupSchedulePatternCreatePayload>
): Promise<GroupSchedulePattern> => {
  const { data } = await conns<GroupSchedulePattern>('erp:group_classes:base', [groupId, 'patterns', patternId]).patch(
    payload
  );
  return data;
};

export const reviseGroupSchedulePattern = async (
  groupId: number,
  patternId: number,
  payload: GroupSchedulePatternRevisionPayload
): Promise<GroupSchedulePatternRevisionResult> => {
  const { data } = await conns<GroupSchedulePatternRevisionResult>('erp:group_classes:base', [
    groupId,
    'patterns',
    patternId,
    'revisions',
  ]).post(payload);
  return data;
};

export const deleteGroupSchedulePattern = async (groupId: number, patternId: number): Promise<void> => {
  await conns<void>('erp:group_classes:base', [groupId, 'patterns', patternId]).delete();
};

export const fetchGroupSchedulePatterns = async (groupId: number): Promise<GroupSchedulePattern[]> => {
  const { data } = await conns<GroupSchedulePattern[]>('erp:group_classes:base', [groupId, 'patterns']).get();
  return data;
};

export const createGroupParticipant = async (
  groupId: number,
  payload: GroupParticipantCreatePayload
): Promise<GroupParticipant> => {
  const { data } = await conns<GroupParticipant>('erp:group_classes:base', [groupId, 'participants']).post(payload);
  return data;
};

export const fetchGroupParticipants = async (groupId: number): Promise<GroupParticipant[]> => {
  const { data } = await conns<GroupParticipant[]>('erp:group_classes:base', [groupId, 'participants']).get();
  return data;
};

export const updateGroupParticipant = async (
  groupId: number,
  participantId: number,
  payload: Partial<GroupParticipantCreatePayload>
): Promise<GroupParticipant> => {
  const { data } = await conns<GroupParticipant>('erp:group_classes:base', [
    groupId,
    'participants',
    participantId,
  ]).patch(payload);
  return data;
};

export const deleteGroupParticipant = async (groupId: number, participantId: number): Promise<void> => {
  await conns<void>('erp:group_classes:base', [groupId, 'participants', participantId]).delete();
};

export const fetchGroupEvent = async (groupEventKey: string): Promise<GroupEventProjection> => {
  const { data } = await conns<GroupEventProjection>('erp:group_classes:events', [groupEventKey]).get();
  return data;
};

export const fetchGroupEventOperationDetail = async (groupEventKey: string): Promise<GroupEventOperationDetail> => {
  const { data } = await conns<GroupEventOperationDetail>('erp:group_classes:operation', [
    groupEventKey,
    'operation',
  ]).get();
  return data;
};

export const createGroupEventParticipant = async (
  payload: GroupEventParticipantCreatePayload
): Promise<GroupEventParticipant> => {
  const { data } = await conns<GroupEventParticipant>('erp:group_classes:participants').post(payload);
  return data;
};

export const createGroupEventOverride = async (
  payload: GroupEventOverrideCreatePayload
): Promise<GroupEventOverride> => {
  const { data } = await conns<GroupEventOverride>('erp:group_classes:overrides').post(payload);
  return data;
};

export const markGroupEventParticipantPresent = async (
  payload: AttendancePresentPayload
): Promise<AttendanceUsageResult> => {
  const { data } = await conns<AttendanceUsageResult>('erp:group_classes:attendance:present').post(payload);
  return data;
};

export const markGroupEventParticipantAbsent = async (
  payload: AttendanceAbsentPayload
): Promise<GroupEventParticipant> => {
  const { data } = await conns<GroupEventParticipant>('erp:group_classes:attendance:absent').post(payload);
  return data;
};

export const resetGroupEventParticipantAttendance = async (
  payload: AttendanceResetPayload
): Promise<GroupEventParticipant> => {
  const { data } = await conns<GroupEventParticipant>('erp:group_classes:attendance:reset').post(payload);
  return data;
};

export const reverseGroupEventAttendanceUsage = async (
  payload: AttendanceReversePayload
): Promise<AttendanceUsageResult> => {
  const { data } = await conns<AttendanceUsageResult>('erp:group_classes:attendance:reverse').post(payload);
  return data;
};

export const createGroupEventDropInOrder = async (payload: DropInOrderPayload): Promise<DropInOrderResult> => {
  const { data } = await conns<DropInOrderResult>('erp:group_classes:billing:drop_in_order').post(payload);
  return data;
};

/**
 * Objetivo: Disponibilizar los turnos como un query reactivo para la UI.
 * Entradas:
 *   - filters: Parámetros que forman parte de la clave del query para segmentar cache.
 *   - options: Configuración avanzada entregada a React Query.
 * Salidas: Resultado tipado de `useQuery` con estados, datos y funciones helper.
 * Requerimientos: Mantener el `staleTime` acotado para permitir actualizaciones frecuentes en agenda.
 */
export const useAppointments = (
  filters?: AppointmentFilters,
  options?: Partial<UseQueryOptions<AppointmentListResponse, Error, AppointmentListResponse>>
): UseQueryResult<AppointmentListResponse, Error> => {
  return useQuery<AppointmentListResponse, Error, AppointmentListResponse>({
    queryKey: filters ? [...appointmentsListKey, filters] : appointmentsListKey,
    queryFn: () => fetchAppointments(filters),
    staleTime: 15_000,
    ...options,
  });
};

export const useAppointmentsExpanded = (
  params: FetchAppointmentsExpandedParams,
  options?: Partial<UseQueryOptions<AppointmentExpandedResponse, Error, AppointmentExpandedResponse>>
): UseQueryResult<AppointmentExpandedResponse, Error> => {
  return useQuery<AppointmentExpandedResponse, Error, AppointmentExpandedResponse>({
    queryKey: [...appointmentsExpandedQueryKey, params],
    queryFn: () => fetchAppointmentsExpanded(params),
    staleTime: 30_000,
    ...options,
  });
};

export const useGroupClasses = (
  options?: Partial<UseQueryOptions<GroupClass[], Error, GroupClass[]>>
): UseQueryResult<GroupClass[], Error> => {
  return useQuery<GroupClass[], Error, GroupClass[]>({
    queryKey: groupClassesQueryKey,
    queryFn: fetchGroupClasses,
    staleTime: 30_000,
    ...options,
  });
};

export const useGroupEvent = (
  groupEventKey: string | null,
  options?: Partial<UseQueryOptions<GroupEventProjection, Error, GroupEventProjection>>
): UseQueryResult<GroupEventProjection, Error> => {
  return useQuery<GroupEventProjection, Error, GroupEventProjection>({
    queryKey: [...groupClassEventsQueryKey, groupEventKey],
    queryFn: () => fetchGroupEvent(groupEventKey ?? ''),
    enabled: Boolean(groupEventKey),
    staleTime: 15_000,
    ...options,
  });
};

export const useGroupEventOperationDetail = (
  groupEventKey: string | null,
  options?: Partial<UseQueryOptions<GroupEventOperationDetail, Error, GroupEventOperationDetail>>
): UseQueryResult<GroupEventOperationDetail, Error> => {
  return useQuery<GroupEventOperationDetail, Error, GroupEventOperationDetail>({
    queryKey: [...groupClassOperationQueryKey, groupEventKey],
    queryFn: () => fetchGroupEventOperationDetail(groupEventKey ?? ''),
    enabled: Boolean(groupEventKey),
    staleTime: 10_000,
    ...options,
  });
};

export const useGroupSchedulePatterns = (
  groupId: number | null,
  options?: Partial<UseQueryOptions<GroupSchedulePattern[], Error, GroupSchedulePattern[]>>
): UseQueryResult<GroupSchedulePattern[], Error> => {
  return useQuery<GroupSchedulePattern[], Error, GroupSchedulePattern[]>({
    queryKey: [...groupClassPatternsQueryKey, groupId],
    queryFn: () => fetchGroupSchedulePatterns(groupId ?? 0),
    enabled: typeof groupId === 'number',
    staleTime: 30_000,
    ...options,
  });
};

export const useCreateGroupClass = (): UseMutationResult<GroupClass, Error, GroupClassCreatePayload> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createGroupClass,
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useUpdateGroupClass = (): UseMutationResult<
  GroupClass,
  Error,
  { groupId: number; payload: GroupClassUpdatePayload }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, payload }) => updateGroupClass(groupId, payload),
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useCreateGroupClassSetup = (): UseMutationResult<
  GroupClassSetupResult,
  Error,
  GroupClassSetupPayload
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createGroupClassSetup,
    onSuccess: (result) => {
      cacheGroupClassSetupResult(client, result);
      invalidateGroupClassQueries(client);
    },
  });
};

export const useRegenerateGroupClassName = (): UseMutationResult<GroupClass, Error, number> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: regenerateGroupClassName,
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useCreateGroupSchedulePattern = (): UseMutationResult<
  GroupSchedulePattern,
  Error,
  { groupId: number; payload: GroupSchedulePatternCreatePayload }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, payload }) => createGroupSchedulePattern(groupId, payload),
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useUpdateGroupSchedulePattern = (): UseMutationResult<
  GroupSchedulePattern,
  Error,
  {
    groupId: number;
    patternId: number;
    payload: Partial<GroupSchedulePatternCreatePayload>;
  }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, patternId, payload }) => updateGroupSchedulePattern(groupId, patternId, payload),
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useReviseGroupSchedulePattern = (): UseMutationResult<
  GroupSchedulePatternRevisionResult,
  Error,
  {
    groupId: number;
    patternId: number;
    payload: GroupSchedulePatternRevisionPayload;
  }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, patternId, payload }) => reviseGroupSchedulePattern(groupId, patternId, payload),
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useDeleteGroupSchedulePattern = (): UseMutationResult<
  void,
  Error,
  { groupId: number; patternId: number }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, patternId }) => deleteGroupSchedulePattern(groupId, patternId),
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useCreateGroupParticipant = (): UseMutationResult<
  GroupParticipant,
  Error,
  { groupId: number; payload: GroupParticipantCreatePayload }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, payload }) => createGroupParticipant(groupId, payload),
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useGroupParticipants = (
  groupId: number | null,
  options?: Partial<UseQueryOptions<GroupParticipant[], Error, GroupParticipant[]>>
): UseQueryResult<GroupParticipant[], Error> => {
  return useQuery<GroupParticipant[], Error, GroupParticipant[]>({
    queryKey: [...groupClassParticipantsQueryKey, groupId],
    queryFn: () => fetchGroupParticipants(groupId ?? 0),
    enabled: typeof groupId === 'number',
    staleTime: 30_000,
    ...options,
  });
};

export const useUpdateGroupParticipant = (): UseMutationResult<
  GroupParticipant,
  Error,
  {
    groupId: number;
    participantId: number;
    payload: Partial<GroupParticipantCreatePayload>;
  }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, participantId, payload }) => updateGroupParticipant(groupId, participantId, payload),
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useDeleteGroupParticipant = (): UseMutationResult<
  void,
  Error,
  { groupId: number; participantId: number }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, participantId }) => deleteGroupParticipant(groupId, participantId),
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useCreateGroupEventParticipant = (): UseMutationResult<
  GroupEventParticipant,
  Error,
  GroupEventParticipantCreatePayload
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createGroupEventParticipant,
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useCreateGroupEventOverride = (): UseMutationResult<
  GroupEventOverride,
  Error,
  GroupEventOverrideCreatePayload
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createGroupEventOverride,
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useMarkGroupEventParticipantPresent = (): UseMutationResult<
  AttendanceUsageResult,
  Error,
  AttendancePresentPayload
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: markGroupEventParticipantPresent,
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useMarkGroupEventParticipantAbsent = (): UseMutationResult<
  GroupEventParticipant,
  Error,
  AttendanceAbsentPayload
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: markGroupEventParticipantAbsent,
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useResetGroupEventParticipantAttendance = (): UseMutationResult<
  GroupEventParticipant,
  Error,
  AttendanceResetPayload
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: resetGroupEventParticipantAttendance,
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useReverseGroupEventAttendanceUsage = (): UseMutationResult<
  AttendanceUsageResult,
  Error,
  AttendanceReversePayload
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: reverseGroupEventAttendanceUsage,
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export const useCreateGroupEventDropInOrder = (): UseMutationResult<DropInOrderResult, Error, DropInOrderPayload> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createGroupEventDropInOrder,
    onSuccess: () => invalidateGroupClassQueries(client),
  });
};

export interface AppointmentServiceLineDraft {
  service_id: number;
  quantity: number;
  duration_minutes?: number | null;
  unit_price?: number | null;
  notes?: string | null;
  /** DEPRECATED: legado, el backend ignora este valor. */
  start_offset_minutes?: number | null;
}

export interface AppointmentDraft {
  customer_id?: number | null;
  staff_user_id?: number | null;
  space_id?: number | null;
  linked_group_id?: string | null;
  start_at: string;
  end_at: string;
  duration_minutes?: number | null;
  notes?: string | null;
  service_lines: AppointmentServiceLineDraft[];
  status?: AppointmentStatus;
}

export interface AppointmentUpdateDraft extends AppointmentDraft {
  version: number;
}

/**
 * Objetivo: Persistir un nuevo turno en el backend.
 * Entradas:
 *   - payload: Datos completos del turno a crear, incluyendo líneas de servicio.
 * Salidas: Promesa que resuelve con el turno creado.
 * Requerimientos: Enviar la información a través del endpoint registrado en `conns`.
 */
export const createAppointment = async (payload: AppointmentDraft): Promise<Appointment> => {
  const { data } = await conns<Appointment>('erp:appointments:list').post(payload);
  return data;
};

/**
 * Objetivo: Actualizar un turno existente respetando el control de versiones.
 * Entradas:
 *   - appointmentId: Identificador único del turno.
 *   - payload: Datos modificados incluyendo la versión actual.
 * Salidas: Promesa con el turno actualizado.
 * Requerimientos: Ejecutar un `PUT` con `conns` para garantizar consistencia.
 */
export const updateAppointment = async (
  appointmentId: number,
  payload: AppointmentUpdateDraft
): Promise<Appointment> => {
  const { data } = await conns<Appointment>('erp:appointments:list', [appointmentId]).put(payload);
  return data;
};

/**
 * Objetivo: Eliminar un turno de la agenda, contemplando control de versiones opcional.
 * Entradas:
 *   - appointmentId: ID del turno a eliminar.
 *   - version: Versión conocida del turno para prevenir colisiones (opcional).
 * Salidas: Promesa que se resuelve sin valor al completar la eliminación.
 * Requerimientos: Invocar al endpoint REST usando el conector estándar y enviar la versión como query param.
 */
export const deleteAppointment = async (appointmentId: number, version?: number): Promise<void> => {
  await conns('erp:appointments:list', [appointmentId]).delete(
    {},
    {
      params: { version },
    }
  );
};

/**
 * Objetivo: Listar asistentes de un turno grupal.
 * Entradas: appointmentId del turno a consultar.
 * Salidas: Lista de asistentes tipada.
 * Requerimientos: Consumir el endpoint de asistentes vía `conns`.
 */
export const fetchAppointmentAttendees = async (appointmentId: number): Promise<AppointmentAttendee[]> => {
  const { data } = await conns<AppointmentAttendee[]>('erp:appointments:attendees', [appointmentId, 'attendees']).get();
  return data;
};

/**
 * Objetivo: Agregar un asistente a un turno grupal.
 * Entradas: appointmentId y payload con el customer_id (y subscription opcional).
 * Salidas: Asistente creado por el backend.
 * Requerimientos: Usar `conns` y mantener tipado estricto.
 */
export const addAppointmentAttendee = async (
  appointmentId: number,
  payload: AppointmentAttendeeCreatePayload
): Promise<AppointmentAttendee> => {
  const { data } = await conns<AppointmentAttendee>('erp:appointments:attendees', [appointmentId, 'attendees']).post(
    payload
  );
  return data;
};

/**
 * Objetivo: Quitar un asistente de un turno grupal.
 * Entradas: appointmentId y attendeeId a eliminar.
 * Salidas: Promesa sin retorno.
 * Requerimientos: Invocar el DELETE correspondiente con `conns`.
 */
export const removeAppointmentAttendee = async (appointmentId: number, attendeeId: number): Promise<void> => {
  await conns('erp:appointments:attendees', [appointmentId, 'attendees', attendeeId]).delete();
};

/**
 * Objetivo: Actualizar el estado de un asistente (confirmed, canceled, no_show).
 * Entradas: appointmentId, attendeeId y nuevo status.
 * Salidas: Asistente actualizado.
 * Requerimientos: Invocar el PATCH correspondiente con `conns`.
 */
export const updateAttendeeStatus = async (
  appointmentId: number,
  attendeeId: number,
  status: 'pending' | 'confirmed' | 'canceled' | 'no_show'
): Promise<AppointmentAttendee> => {
  const { data } = await conns<AppointmentAttendee>('erp:appointments:attendees', [
    appointmentId,
    'attendees',
    attendeeId,
  ]).patch({ status });
  return data;
};

/**
 * Objetivo: Proveer un hook de mutación para crear turnos y refrescar la agenda en caché.
 * Entradas: No recibe argumentos externos, pero la mutación espera un `AppointmentDraft`.
 * Salidas: Resultado de `useMutation` con helpers para disparar la creación.
 * Requerimientos: Invalidar la lista de turnos al finalizar exitosamente la mutación.
 */
export const useCreateAppointment = (): UseMutationResult<Appointment, Error, AppointmentDraft> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createAppointment,
    onSuccess: () => {
      invalidateAppointmentsQueries(client);
    },
  });
};

/**
 * Objetivo: Gestionar la actualización de turnos a través de React Query.
 * Entradas: La mutación espera un objeto con `appointmentId` y `payload`.
 * Salidas: Resultado de `useMutation` que permite ejecutar y monitorear la operación.
 * Requerimientos: Invalidate la lista de turnos tras una actualización exitosa.
 */
export const useUpdateAppointment = (): UseMutationResult<
  Appointment,
  Error,
  { appointmentId: number; payload: AppointmentUpdateDraft }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, payload }) => updateAppointment(appointmentId, payload),
    onSuccess: () => {
      invalidateAppointmentsQueries(client);
    },
  });
};

/**
 * Objetivo: Asignar splits de planificación a profesionales y recursos.
 * Entradas:
 *   - payload: Datos de planificación calculados en el frontend.
 * Salidas: Promesa con la respuesta del backend, incluyendo conflictos o ajustes.
 * Requerimientos: Consumir el endpoint `planning:assign` mediante `conns`.
 */
export const assignPlanning = async (payload: PlanningDominoRequest): Promise<PlanningDominoResponse> => {
  const { data } = await conns<PlanningDominoResponse>('erp:appointments:planning:assign').post(payload);
  return data;
};

/**
 * Objetivo: Persistir el reordenamiento de la planificación existente.
 * Entradas:
 *   - payload: Estructura de planificación resultante tras reordenar.
 * Salidas: Promesa con la respuesta del backend.
 * Requerimientos: Ejecutar un `PATCH` al endpoint configurado en `conns`.
 */
export const reorderPlanning = async (payload: PlanningDominoRequest): Promise<PlanningDominoResponse> => {
  const { data } = await conns<PlanningDominoResponse>('erp:appointments:planning:reorder').patch(payload);
  return data;
};

/**
 * Objetivo: Facilitar la asignación de planificación desde la UI mediante una mutación React Query.
 * Entradas: La mutación espera un `PlanningDominoRequest` completo.
 * Salidas: Resultado de `useMutation` que expone estados y helpers.
 * Requerimientos: Reutilizar `assignPlanning` como función subyacente.
 */
export const useAssignPlanning = (): UseMutationResult<PlanningDominoResponse, Error, PlanningDominoRequest> => {
  return useMutation({
    mutationFn: assignPlanning,
  });
};

/**
 * Objetivo: Controlar la eliminación de turnos y sincronizar el listado en cache.
 * Entradas: La mutación recibe el `appointmentId` y la `version` opcional.
 * Salidas: Resultado de `useMutation` para invocar la operación desde componentes.
 * Requerimientos: Invalidar la agenda una vez completada la eliminación para refrescar la UI.
 */
export const useDeleteAppointment = (): UseMutationResult<void, Error, { appointmentId: number; version?: number }> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, version }) => deleteAppointment(appointmentId, version),
    onSuccess: () => {
      invalidateAppointmentsQueries(client);
    },
  });
};

/**
 * Objetivo: Cancelar un turno respetando las reglas de negocio del backend.
 * Entradas:
 *   - appointmentId: Identificador del turno.
 *   - payload: Información adicional requerida (por ejemplo, versión).
 * Salidas: Promesa con el turno ya cancelado.
 * Requerimientos: Ejecutar una llamada POST al endpoint especializado de cancelación.
 */
const cancelAppointment = async (appointmentId: number, payload: AppointmentCancelPayload): Promise<Appointment> => {
  const { data } = await conns<Appointment>('erp:appointments:actions:cancel', [appointmentId, 'cancel']).post(payload);
  return data;
};

const reactivateAppointment = async (
  appointmentId: number,
  payload: AppointmentReactivatePayload
): Promise<Appointment> => {
  const { data } = await conns<Appointment>('erp:appointments:actions:reactivate', [appointmentId, 'reactivate']).post(
    payload
  );
  return data;
};

/**
 * Objetivo: Reemplazar en memoria un turno actualizado dentro de la respuesta cacheada.
 * Entradas:
 *   - list: Respuesta cacheada actual (puede ser `undefined`).
 *   - updated: Turno actualizado recibido desde el backend.
 * Salidas: Nueva respuesta con el turno reemplazado o la lista original si no se encontró coincidencia.
 * Requerimientos: Mantener la inmutabilidad de la lista original para no romper React Query.
 */
const replaceAppointmentInList = (
  list: AppointmentListResponse | undefined,
  updated: Appointment
): AppointmentListResponse | undefined => {
  if (!list) {
    return list;
  }
  const index = list.appointments.findIndex((item) => item.id === updated.id);
  if (index === -1) {
    return list;
  }
  const nextAppointments = [...list.appointments];
  nextAppointments[index] = updated;
  return { ...list, appointments: nextAppointments };
};

const replaceAppointmentsInList = (
  list: AppointmentListResponse | undefined,
  updatedAppointments: Appointment[]
): AppointmentListResponse | undefined => {
  if (!list || updatedAppointments.length === 0) {
    return list;
  }
  const updatedById = new Map(updatedAppointments.map((appointment) => [appointment.id, appointment]));
  let changed = false;
  const nextAppointments = list.appointments.map((appointment) => {
    const updated = updatedById.get(appointment.id);
    if (!updated) return appointment;
    changed = true;
    return updated;
  });
  return changed ? { ...list, appointments: nextAppointments } : list;
};

/**
 * Objetivo: Cancelar turnos desde la UI y mantener la cache sincronizada con la respuesta del backend.
 * Entradas: La mutación requiere el `appointmentId` y el payload de cancelación.
 * Salidas: Resultado de `useMutation` con acceso al turno cancelado.
 * Requerimientos: Actualizar optimistamente la cache y forzar la revalidación al finalizar.
 */
export const useCancelAppointment = (): UseMutationResult<
  Appointment,
  Error,
  { appointmentId: number; payload: AppointmentCancelPayload }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, payload }) => cancelAppointment(appointmentId, payload),
    onSuccess: (data) => {
      client.setQueriesData<AppointmentListResponse | undefined>({ queryKey: appointmentsListKey }, (cached) =>
        replaceAppointmentInList(cached, data)
      );
    },
    onSettled: () => {
      invalidateAppointmentsQueries(client);
    },
  });
};

export const useReactivateAppointment = (): UseMutationResult<
  Appointment,
  Error,
  { appointmentId: number; payload: AppointmentReactivatePayload }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, payload }) => reactivateAppointment(appointmentId, payload),
    onSuccess: (data) => {
      client.setQueriesData<AppointmentListResponse | undefined>({ queryKey: appointmentsListKey }, (cached) =>
        replaceAppointmentInList(cached, data)
      );
    },
    onSettled: () => {
      invalidateAppointmentsQueries(client);
    },
  });
};

/**
 * Objetivo: Generar cargos asociados a un turno, incluyendo ventas vinculadas.
 * Entradas:
 *   - appointmentId: Identificador del turno a cobrar.
 *   - payload: Información comercial para emitir la venta.
 * Salidas: Promesa con el turno actualizado y el pedido generado.
 * Requerimientos: Invocar el endpoint `actions:charge` y devolver la respuesta tipada.
 */
const chargeAppointment = async (
  appointmentId: number,
  payload: AppointmentChargePayload
): Promise<AppointmentChargeResponse> => {
  const { data } = await conns<AppointmentChargeResponse>('erp:appointments:actions:charge', [
    appointmentId,
    'charge',
  ]).post(payload);
  return data;
};

const chargeLinkedAppointments = async (
  appointmentId: number,
  payload: AppointmentLinkedChargePayload
): Promise<AppointmentLinkedChargeResponse> => {
  const { data } = await conns<AppointmentLinkedChargeResponse>('erp:appointments:actions:charge', [
    appointmentId,
    'charge-linked',
  ]).post(payload);
  return data;
};

/**
 * Objetivo: Permitir desde la UI el cobro de un turno y propagar los cambios en agenda.
 * Entradas: La mutación recibe `appointmentId` y el payload de cobro.
 * Salidas: Resultado de `useMutation` con la respuesta completa del backend.
 * Requerimientos: Reemplazar el turno en cache con la versión actualizada y revalidar la lista.
 */
export const useChargeAppointment = (): UseMutationResult<
  AppointmentChargeResponse,
  Error,
  { appointmentId: number; payload: AppointmentChargePayload }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, payload }) => chargeAppointment(appointmentId, payload),
    onSuccess: (response) => {
      const updatedAppointment = response.appointment;
      client.setQueriesData<AppointmentListResponse | undefined>({ queryKey: appointmentsListKey }, (cached) =>
        replaceAppointmentInList(cached, updatedAppointment)
      );
    },
    onSettled: () => {
      invalidateAppointmentsQueries(client);
    },
  });
};

export const useChargeLinkedAppointments = (): UseMutationResult<
  AppointmentLinkedChargeResponse,
  Error,
  { appointmentId: number; payload: AppointmentLinkedChargePayload }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, payload }) => chargeLinkedAppointments(appointmentId, payload),
    onSuccess: (response) => {
      client.setQueriesData<AppointmentListResponse | undefined>({ queryKey: appointmentsListKey }, (cached) =>
        replaceAppointmentsInList(cached, response.appointments)
      );
    },
    onSettled: () => {
      invalidateAppointmentsQueries(client);
    },
  });
};

/**
 * Objetivo: Generar una clave consistente de React Query para el historial de un turno.
 * Entradas:
 *   - appointmentId: Identificador del turno cuyo historial se consulta.
 * Salidas: Tupla inmutable que sirve como `queryKey`.
 * Requerimientos: Mantener formato estable para reutilización en invalidaciones.
 */
const appointmentHistoryKey = (appointmentId: number) => ['erp', 'appointments', 'history', appointmentId] as const;

export interface AppointmentHistoryDraft {
  staff_user_id: number;
  duration_minutes?: number | null;
  notes?: string | null;
  sale_item_id?: number | null;
  follow_up_appointment_id?: number | null;
}

/**
 * Objetivo: Obtener el historial de acciones sobre un turno específico.
 * Entradas:
 *   - appointmentId: ID del turno.
 * Salidas: Promesa con la lista de eventos históricos.
 * Requerimientos: Consumir el endpoint `history` a través del conector común.
 */
export const fetchAppointmentHistory = async (appointmentId: number): Promise<AppointmentHistoryListResponse> => {
  const { data } = await conns<AppointmentHistoryListResponse>('erp:appointments:history', [
    appointmentId,
    'history',
  ]).get();
  return data;
};

/**
 * Objetivo: Exponer el historial de un turno mediante un query con habilitación condicional.
 * Entradas:
 *   - appointmentId: Identificador opcional; si es nulo el query queda deshabilitado.
 *   - options: Configuración adicional para el query.
 * Salidas: Resultado de `useQuery` con control de habilitación.
 * Requerimientos: Lanzar error si se intenta ejecutar sin `appointmentId` válido.
 */
export const useAppointmentHistory = (
  appointmentId: number | null,
  options?: Partial<UseQueryOptions<AppointmentHistoryListResponse, Error, AppointmentHistoryListResponse>>
): UseQueryResult<AppointmentHistoryListResponse, Error> => {
  return useQuery<AppointmentHistoryListResponse, Error, AppointmentHistoryListResponse>({
    queryKey: appointmentId ? appointmentHistoryKey(appointmentId) : ['erp', 'appointments', 'history'],
    queryFn: () => {
      if (!appointmentId) {
        throw new Error('appointmentId is required');
      }
      return fetchAppointmentHistory(appointmentId);
    },
    enabled: Boolean(appointmentId),
    staleTime: 5_000,
    ...options,
  });
};

/**
 * Objetivo: Registrar una nueva entrada en el historial del turno.
 * Entradas:
 *   - appointmentId: Turno objetivo.
 *   - payload: Datos a persistir (nota, duración, vínculos, etc.).
 * Salidas: Promesa con la entrada creada.
 * Requerimientos: Usar `conns` para invocar el endpoint `history` en modo POST.
 */
export const createAppointmentHistoryEntry = async (
  appointmentId: number,
  payload: AppointmentHistoryDraft
): Promise<AppointmentHistoryEntry> => {
  const { data } = await conns<AppointmentHistoryEntry>('erp:appointments:history', [appointmentId, 'history']).post(
    payload
  );
  return data;
};

/**
 * Objetivo: Gestionar la creación de eventos históricos desde el frontend.
 * Entradas: La mutación requiere `appointmentId` y el payload de historial.
 * Salidas: Resultado de `useMutation` con acceso a la entrada creada.
 * Requerimientos: Invalidar el query correspondiente al historial del turno afectado.
 */
export const useCreateAppointmentHistoryEntry = (): UseMutationResult<
  AppointmentHistoryEntry,
  Error,
  { appointmentId: number; payload: AppointmentHistoryDraft }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, payload }) => createAppointmentHistoryEntry(appointmentId, payload),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({
        queryKey: appointmentHistoryKey(variables.appointmentId),
      });
    },
  });
};

export const updateAppointmentHistoryEntry = async (
  appointmentId: number,
  entryId: number,
  payload: AppointmentHistoryDraft
): Promise<AppointmentHistoryEntry> => {
  const { data } = await conns<AppointmentHistoryEntry>('erp:appointments:history', [
    appointmentId,
    'history',
    entryId,
  ]).patch(payload);
  return data;
};

export const useUpdateAppointmentHistoryEntry = (): UseMutationResult<
  AppointmentHistoryEntry,
  Error,
  { appointmentId: number; entryId: number; payload: AppointmentHistoryDraft }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, entryId, payload }) => updateAppointmentHistoryEntry(appointmentId, entryId, payload),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({
        queryKey: appointmentHistoryKey(variables.appointmentId),
      });
    },
  });
};

export const deleteAppointmentHistoryEntry = async (appointmentId: number, entryId: number): Promise<void> => {
  await conns('erp:appointments:history', [appointmentId, 'history', entryId]).delete();
};

export const useDeleteAppointmentHistoryEntry = (): UseMutationResult<
  void,
  Error,
  { appointmentId: number; entryId: number }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, entryId }) => deleteAppointmentHistoryEntry(appointmentId, entryId),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({
        queryKey: appointmentHistoryKey(variables.appointmentId),
      });
    },
  });
};

export interface AvailabilityWindow {
  start_at: string;
  end_at: string;
}

export interface AppointmentAvailabilityResponse {
  staff_user_id: number;
  service_id: number;
  space_id: number;
  start_date: string;
  end_date: string;
  duration_minutes: number;
  windows: AvailabilityWindow[];
}

const availabilityKey = ['erp', 'appointments', 'availability'] as const;

export interface AvailabilityParams {
  staff_user_id: number;
  service_id: number;
  start_date: string;
  end_date: string;
  space_id?: number;
}

/**
 * Objetivo: Consultar ventanas de disponibilidad para un profesional/servicio.
 * Entradas:
 *   - params: Parámetros obligatorios que definen profesional, servicio y rango temporal.
 * Salidas: Promesa con ventanas disponibles (`AppointmentAvailabilityResponse`).
 * Requerimientos: Realizar la llamada GET al endpoint de disponibilidad usando `conns`.
 */
export const fetchAvailability = async (params: AvailabilityParams): Promise<AppointmentAvailabilityResponse> => {
  const { data } = await conns<AppointmentAvailabilityResponse>('erp:appointments:availability').get(params);
  return data;
};

/**
 * Objetivo: Entregar la disponibilidad como query reactivo con habilitación condicional.
 * Entradas:
 *   - params: Parámetros requeridos; si son `undefined`, el query queda deshabilitado.
 *   - options: Configuraciones adicionales opcionales.
 * Salidas: Resultado tipado de `useQuery` con datos de disponibilidad.
 * Requerimientos: Lanzar error si se ejecuta sin parámetros válidos y controlar el `enabled`.
 */
export const useAvailability = (
  params: AvailabilityParams | undefined,
  options?: Partial<UseQueryOptions<AppointmentAvailabilityResponse, Error, AppointmentAvailabilityResponse>>
): UseQueryResult<AppointmentAvailabilityResponse, Error> => {
  return useQuery<AppointmentAvailabilityResponse, Error, AppointmentAvailabilityResponse>({
    queryKey: params ? [...availabilityKey, params] : availabilityKey,
    queryFn: () => {
      if (!params) {
        throw new Error('Availability params are required');
      }
      return fetchAvailability(params);
    },
    enabled: Boolean(params),
    staleTime: 5_000,
    ...options,
  });
};

export type { AppointmentStatus };
